import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IspvisionService, VisionWs } from '../../../core/services/ispvision.service';
import { TableService } from 'src/app/core/services/table.service';
import { TableColumn } from 'src/app/core/models/table.model';
import { Breakpoint } from 'src/app/core/services/responsive.service';
import { RowItem } from 'src/app/core/models/item.model';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { NavigationService } from '../../../../../src/app/core/services/navigation.service';

@Component({
    selector: 'orders',
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.less'],
})
export class OrdersComponent implements OnInit {
    public orders: any[] = [];
    public filterSearch: string = '';
    public filterStatus: string = '';
    public statusOptions: string[] = [];
    public navPath: string = 'markiezen';

    constructor(
        public visionWs: IspvisionService,
        public tableService: TableService,
        private currencyPipe: CurrencyPipe,
        private datePipe: DatePipe,
        public navigation: NavigationService,
        private route: ActivatedRoute
    ) {
        this.route.data.subscribe((data) => {
            if (data['type'] === 'kokers') {
                this.navPath = 'kokers-en-profielen';
            }
            this.visionWs.setFormPartitioning(data['type']);
        });
    }

    async ngOnInit(): Promise<void> {
        this.orders = await this.visionWs.getOrders();
        this.tableService.register(
            {
                paginator: true,
                breakpoint: { column: [Breakpoint.MD], row: [Breakpoint.MD] },
            },
            this.mapColumns.bind(this),
            this.mapInvoicesToTable.bind(this),
            this.orders
        );

        this.tableService.addSearches([
            { searchOn: 'order_id', accuracy: 80 },
            { searchOn: 'order_ref', accuracy: 80 },
            { searchOn: 'name', accuracy: 80 },
            { searchOn: 'reference', accuracy: 80 },
            { searchOn: 'created_at', accuracy: 80 },
            { searchOn: 'status', accuracy: 100 },
            { searchOn: 'netto_grand_total', accuracy: 80 },
        ]);

        this.tableService.addFilter([
            {
                filterOn: 'status',
                selectAll: false,
                filteredOn: this.filterStatus,
            },
        ]);
        this.tableService.search();
    }

    private mapColumns(): TableColumn[] {
        return [
            {
                item: { type: 'text', text: 'Ref nr.' },
                sortOn: 'order_id',
                width: 7,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'Referentie' },
                sortOn: 'reference',
                width: 18,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 80,
                },
            },
            {
                item: { type: 'text', text: 'Order nr.' },
                sortOn: 'order_ref',
                width: 7,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'Orderdatum' },
                sortOn: 'created_at',
                width: 10,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'Verkoper' },
                sortOn: 'name',
                width: 10,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 80,
                },
            },
            {
                item: { type: 'text', text: 'Status' },
                sortOn: 'status',
                width: 12,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 80,
                },
            },
            {
                item: { type: 'text', text: 'Verwachte leverdatum' },
                sortOn: 'is_concept',
                width: 15,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 80,
                },
            },
            {
                item: { type: 'text', text: 'Totaalprijs' },
                sortOn: 'netto_grand_total',
                width: 7,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    widthUntil: 40,
                },
            },
            {
                item: { type: 'text', text: 'Details' },
                position: 'center',
                width: 10,
                breakpoint: {
                    breakpoint: Breakpoint.MD,
                    state: 'after',
                },
            },
        ];
    }

    /**
     * Map invoices to table format
     *
     * @author    Arjan Koemans <arjan@safira.nl>
     */
    private mapInvoicesToTable(order: {
        order_id: number;
        order_ref: string;
        created_at: string;
        name: string;
        order: string;
        reference: string;
        status: string | null;
        delivery_date: string | null;
        netto_grand_total: number;
        is_concept: boolean;
    }): RowItem {
        return {
            type: 'row',
            expandable: {
                breakpoint: Breakpoint.MD,
                colspan: 2,
                gap: 10,
                closedHeight: 16.8,
                openHeight: 83.6,
            },
            items: [
                {
                    type: 'text',
                    text: order.order_id,
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: order.reference,
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: order.order_ref,
                    expansion: 'hidden',
                },
                {
                    type: 'text',
                    text: this.datePipe.transform(order.created_at, 'dd-MM-yyyy'),
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: order.name,
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: order.status ?? '',
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: order.delivery_date
                        ? (this.datePipe.transform(order.delivery_date, 'dd-MM-yyyy') ?? '')
                        : '',
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'text',
                    text: this.currencyPipe.transform(order.netto_grand_total, 'EUR', 'symbol'),
                    expansion: 'hidden',
                    action: {
                        type: 'route',
                        route: '/' + this.navPath + '/orders/' + order.order_id,
                    },
                },
                {
                    type: 'group',
                    position: 'center',
                    expansion: 'expandable',
                    items: [
                        {
                            type: 'text',
                            icon: this.navPath === 'markiezen' ? 'info' : 'pencil',
                            iconHover: true,
                            iconBorder: true,
                            action: {
                                type: 'route',
                                route: '/' + this.navPath + '/orders/' + order.order_id,
                            },
                            title: 'global_edit',
                        },
                    ],
                },
            ],
        };
    }

    public getOrder(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target.tagName === 'TD') {
            window.location.href = target.getAttribute('data-action');
        }
    }
}
